import type { ReactNode } from "react";
import {
  Children,
  createContext,
  createElement,
  isValidElement,
  useCallback,
  useContext,
} from "react";
import type {
  DataTableBaseProps,
  ExtractRecordPaths,
  HintedString,
  Identifier,
  RaRecord,
  SortPayload,
} from "ra-core";
import {
  DataTableBase,
  DataTableRenderContext,
  FieldTitle,
  RecordContextProvider,
  useDataTableCallbacksContext,
  useDataTableConfigContext,
  useDataTableDataContext,
  useDataTableRenderContext,
  useDataTableSelectedIdsContext,
  useDataTableSortContext,
  useDataTableStoreContext,
  useGetPathForRecordCallback,
  useRecordContext,
  useResourceContext,
  useStore,
  useTranslate,
  useTranslateLabel,
} from "ra-core";
import { useNavigate } from "react-router";
import { ArrowDownAZ, ArrowUpZA } from "lucide-react";
import get from "lodash/get";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ColumnsSelector,
  ColumnsSelectorItem,
} from "@/components/admin/columns-button";
import { NumberField } from "@/components/admin/number-field";
import {
  BulkActionsToolbar,
  BulkActionsToolbarChildren,
} from "@/components/admin/bulk-actions-toolbar";
import { ColumnResizeHandle } from "@/components/admin/column-resize-handle";
import { TableSkeleton } from "@/components/admin/loading-skeletons";
import { NAV_ROW_ATTR } from "@/components/admin/shortcut-attrs";
import {
  isPlainRowClick,
  ROW_ID_ATTR,
  useRowSelect,
} from "@/components/admin/row-select-context";
import {
  ColumnWidthResetContext,
  useColumnWidths,
  type ColumnWidths,
} from "@/hooks/useColumnWidths";

const defaultBulkActionButtons = <BulkActionsToolbarChildren />;

// Bredden på utvalgskolonnen (`w-8`), som ikke er en vanlig kolonne.
const SELECT_COLUMN_WIDTH = 32;
// Bredde en kolonne uten oppgitt `width` får i minsteberegningen. Selve
// kolonnen får ingen `<col width>` – den deler resten av plassen likt med de
// andre uten bredde, slik table-fixed gjør.
const IMPLICIT_COLUMN_WIDTH = 140;

/**
 * Kolonnebredder deles med hodecellene gjennom en egen kontekst i stedet for
 * props: DataTable.Col rendres gjennom ra-cores render-kontekst og får ikke
 * noe fra forelderen sin.
 */
const ColumnWidthContext = createContext<{
  widths: ColumnWidths;
  startResize: (
    key: string,
  ) => (event: React.PointerEvent<HTMLElement>) => void;
} | null>(null);

const columnPropsOf = (children: ReactNode): DataTableColumnProps[] =>
  Children.toArray(children)
    .filter(isValidElement)
    .map((child) => child.props as DataTableColumnProps);

/**
 * `<colgroup>` som gir tabellen faste kolonnebredder.
 *
 * Skjulte kolonner filtreres med nøyaktig samme regel som hode- og datacellene
 * (`hiddenColumns.includes(source)`), og rekkefølgen kommer fra de allerede
 * omrokerte children-e. Ellers ville colgroup-en og cellene kommet i utakt, og
 * bredden havnet på feil kolonne.
 */
const DataTableColgroup = ({ children }: { children: ReactNode }) => {
  const { hasBulkActions = false } = useDataTableConfigContext();
  const { storeKey, defaultHiddenColumns } = useDataTableStoreContext();
  const [hiddenColumns] = useStore<string[]>(storeKey, defaultHiddenColumns);
  const columnWidths = useContext(ColumnWidthContext);
  const columns = columnPropsOf(children).filter(
    (column) => !hiddenColumns.includes(column.source!),
  );
  return (
    <colgroup>
      {hasBulkActions ? <col style={{ width: SELECT_COLUMN_WIDTH }} /> : null}
      {columns.map((column, index) => {
        const width =
          (column.source ? columnWidths?.widths[column.source] : undefined) ??
          column.width;
        return (
          <col
            key={column.source ?? `col${index}`}
            style={width ? { width } : undefined}
          />
        );
      })}
    </colgroup>
  );
};

/**
 * Minstebredde på selve `<table>`, slik at containeren ruller vannrett i
 * stedet for å klemme kolonnene sammen når summen ikke får plass.
 */
const useTableMinWidth = (children: ReactNode, widths: ColumnWidths) => {
  const { hasBulkActions = false } = useDataTableConfigContext();
  const { storeKey, defaultHiddenColumns } = useDataTableStoreContext();
  const [hiddenColumns] = useStore<string[]>(storeKey, defaultHiddenColumns);
  return columnPropsOf(children)
    .filter((column) => !hiddenColumns.includes(column.source!))
    .reduce(
      (sum, column) =>
        sum +
        ((column.source ? widths[column.source] : undefined) ??
          column.width ??
          IMPLICIT_COLUMN_WIDTH),
      hasBulkActions ? SELECT_COLUMN_WIDTH : 0,
    );
};

/**
 * A powerful data table with sorting, selection, and column customization.
 *
 * Displays records in a table with built-in support for column sorting, bulk selection, row clicks,
 * and column visibility controls. Use DataTable.Col to define columns.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/datatable/ DataTable documentation}
 *
 * @example
 * import { List, DataTable, ReferenceField, EditButton } from '@/components/admin';
 *
 * export const PostList = () => (
 *   <List>
 *     <DataTable>
 *       <DataTable.Col source="id" />
 *       <DataTable.Col label="User">
 *         <ReferenceField source="user_id" reference="users" />
 *       </DataTable.Col>
 *       <DataTable.Col source="title" />
 *       <DataTable.Col>
 *         <EditButton />
 *       </DataTable.Col>
 *     </DataTable>
 *   </List>
 * );
 */
export function DataTable<RecordType extends RaRecord = RaRecord>(
  props: DataTableProps<RecordType>,
) {
  const {
    children,
    className,
    rowClassName,
    bulkActionButtons = defaultBulkActionButtons,
    bulkActionsToolbar,
    stickyFirstColumn = false,
    ...rest
  } = props;
  const hasBulkActions = !!bulkActionsToolbar || bulkActionButtons !== false;
  const resourceFromContext = useResourceContext(props);
  const storeKey = props.storeKey || `${resourceFromContext}.datatable`;
  const [columnRanks] = useStore<number[]>(`${storeKey}_columnRanks`);
  const columns = columnRanks
    ? reorderChildren(children, columnRanks)
    : children;
  const { widths, hasCustomWidths, startResize, reset } =
    useColumnWidths(storeKey);

  return (
    <DataTableBase<RecordType>
      hasBulkActions={hasBulkActions}
      loading={
        <TableSkeleton
          columns={Math.min(Math.max(Children.count(children), 3), 6)}
        />
      }
      empty={<DataTableEmpty />}
      {...rest}
    >
      <ColumnWidthContext.Provider value={{ widths, startResize }}>
        <DataTableFrame<RecordType>
          className={className}
          rowClassName={rowClassName}
          columns={columns}
          widths={widths}
          stickyFirstColumn={stickyFirstColumn}
          hasBulkActions={hasBulkActions}
        />
        {bulkActionsToolbar ??
          (bulkActionButtons !== false && (
            <BulkActionsToolbar>
              {isValidElement(bulkActionButtons)
                ? bulkActionButtons
                : defaultBulkActionButtons}
            </BulkActionsToolbar>
          ))}
        <ColumnWidthResetContext.Provider value={{ reset, hasCustomWidths }}>
          <DataTableRenderContext.Provider value="columnsSelector">
            <ColumnsSelector>{children}</ColumnsSelector>
          </DataTableRenderContext.Provider>
        </ColumnWidthResetContext.Provider>
      </ColumnWidthContext.Provider>
    </DataTableBase>
  );
}

/**
 * Selve tabellen. Egen komponent fordi den må ligge inne i DataTableBase for
 * å nå config- og store-kontekstene som avgjør hvilke kolonner som vises.
 */
const DataTableFrame = <RecordType extends RaRecord = RaRecord>({
  className,
  rowClassName,
  columns,
  widths,
  stickyFirstColumn,
  hasBulkActions,
}: {
  className?: string;
  rowClassName?: (record: RecordType) => string | undefined;
  columns: ReactNode;
  widths: ColumnWidths;
  stickyFirstColumn: boolean;
  hasBulkActions: boolean;
}) => {
  const minWidth = useTableMinWidth(columns, widths);
  return (
    <div className={cn("rounded-md border", className)}>
      {/* table-fixed: uten den bestemmer innholdet bredden, og en dratt
          kolonnebredde ville blitt ignorert. */}
      {/* data-sticky-first: første kolonne (og avkrysningen) står fast når
          tabellen rulles sidelengs, se .data-table-sticky i index.css. */}
      <Table
        className="table-fixed"
        style={{ minWidth }}
        data-sticky-first={stickyFirstColumn || undefined}
        data-bulk={hasBulkActions || undefined}
      >
        <DataTableColgroup>{columns}</DataTableColgroup>
        <DataTableRenderContext.Provider value="header">
          <DataTableHead>{columns}</DataTableHead>
        </DataTableRenderContext.Provider>
        <DataTableBody<RecordType> rowClassName={rowClassName}>
          {columns}
        </DataTableBody>
        <DataTableFoot>{columns}</DataTableFoot>
      </Table>
    </div>
  );
};

DataTable.Col = DataTableColumn;
DataTable.NumberCol = DataTableNumberColumn;

/**
 * Sumrad nederst. Vises bare når minst én kolonne har `footer`. Cellene
 * bygges fra kolonnenes props, med samme skjul-regel og rekkefølge som
 * colgroup-en, så summen havner under riktig kolonne også når brukeren har
 * skjult eller flyttet kolonner.
 */
const DataTableFoot = ({ children }: { children: ReactNode }) => {
  const { hasBulkActions = false } = useDataTableConfigContext();
  const { storeKey, defaultHiddenColumns } = useDataTableStoreContext();
  const [hiddenColumns] = useStore<string[]>(storeKey, defaultHiddenColumns);
  const columns = columnPropsOf(children).filter(
    (column) => !hiddenColumns.includes(column.source!),
  );
  if (!columns.some((column) => column.footer != null)) return null;
  return (
    <TableFooter>
      <TableRow className="hover:bg-transparent">
        {hasBulkActions ? <TableCell className="w-8" /> : null}
        {columns.map((column, index) => (
          <TableCell
            key={column.source ?? `col${index}`}
            className={cn(
              "overflow-hidden py-2 text-ellipsis",
              column.footerClassName,
            )}
          >
            {column.footer}
          </TableCell>
        ))}
      </TableRow>
    </TableFooter>
  );
};

const DataTableHead = ({ children }: { children: ReactNode }) => {
  const data = useDataTableDataContext();
  const { hasBulkActions = false } = useDataTableConfigContext();
  const { onSelect } = useDataTableCallbacksContext();
  const selectedIds = useDataTableSelectedIdsContext();
  const handleToggleSelectAll = (checked: boolean) => {
    if (!onSelect || !data || !selectedIds) return;
    onSelect(
      checked
        ? selectedIds.concat(
            data
              .filter((record) => !selectedIds.includes(record.id))
              .map((record) => record.id),
          )
        : // We should only unselect the ids present in the current page
          selectedIds.filter((id) => !data.some((record) => record.id === id)),
    );
  };
  const selectableIds = Array.isArray(data)
    ? data.map((record) => record.id)
    : [];
  return (
    <TableHeader>
      <TableRow>
        {hasBulkActions ? (
          <TableHead className="w-8">
            <Checkbox
              onCheckedChange={handleToggleSelectAll}
              checked={
                selectedIds &&
                selectedIds.length > 0 &&
                selectableIds.length > 0 &&
                selectableIds.every((id) => selectedIds.includes(id))
              }
              className="mb-2"
            />
          </TableHead>
        ) : null}
        {children}
      </TableRow>
    </TableHeader>
  );
};

const DataTableBody = <RecordType extends RaRecord = RaRecord>({
  children,
  rowClassName,
}: {
  children: ReactNode;
  rowClassName?: (record: RecordType) => string | undefined;
}) => {
  const data = useDataTableDataContext();
  return (
    <TableBody>
      {data?.map((record, rowIndex) => (
        <RecordContextProvider
          value={record}
          key={record.id ?? `row${rowIndex}`}
        >
          <DataTableRow className={rowClassName?.(record)}>
            {children}
          </DataTableRow>
        </RecordContextProvider>
      ))}
    </TableBody>
  );
};

const DataTableRow = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { rowClick, handleToggleItem } = useDataTableCallbacksContext();
  const selectedIds = useDataTableSelectedIdsContext();
  const { hasBulkActions = false } = useDataTableConfigContext();

  const record = useRecordContext();
  if (!record) {
    throw new Error("DataTableRow can only be used within a RecordContext");
  }

  const resource = useResourceContext();
  if (!resource) {
    throw new Error("DataTableRow can only be used within a ResourceContext");
  }

  const navigate = useNavigate();
  const getPathForRecord = useGetPathForRecordCallback();

  const handleToggle = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!handleToggleItem) return;
      handleToggleItem(record.id, event);
    },
    [handleToggleItem, record.id],
  );

  const rowSelect = useRowSelect();
  const isSelected =
    rowSelect?.selectedId != null &&
    String(rowSelect.selectedId) === String(record.id);
  const clickable = rowClick !== false || !!rowSelect;

  const handleClick = useCallback(
    async (fromKeyboard = false) => {
      if (rowSelect) {
        rowSelect.select(record.id, { fromKeyboard });
        return;
      }
      const temporaryLink =
        typeof rowClick === "function"
          ? rowClick(record.id, resource, record)
          : rowClick;

      const link = isPromise(temporaryLink)
        ? await temporaryLink
        : temporaryLink;

      const path = await getPathForRecord({
        record,
        resource,
        link,
      });
      if (path === false || path == null) {
        return;
      }
      navigate(path, {
        state: { _scrollToTop: true },
      });
    },
    [record, resource, rowClick, navigate, getPathForRecord, rowSelect],
  );

  return (
    <TableRow
      key={record.id}
      onClick={(event) => {
        // Med detaljpanel: bare klikk på selve raden, ikke i lenker,
        // nedtrekk og felt inni den.
        if (rowSelect && !isPlainRowClick(event.currentTarget, event.target))
          return;
        if (!rowSelect && rowClick === false) return;
        void handleClick();
      }}
      aria-selected={rowSelect ? isSelected : undefined}
      data-selected={isSelected || undefined}
      {...(rowSelect ? { [ROW_ID_ATTR]: String(record.id) } : {})}
      // Klikkbare rader kan nås med J/K (tastatursnarveier) og åpnes med
      // Enter. tabIndex -1: de er fokuserbare uten å havne i tab-rekkefølgen.
      // Rader med rowClick={false} (oppgavelista) har J/K på tittellenken,
      // så de får ikke tastaturnavigasjon her selv om de kan åpne panelet.
      {...(rowClick !== false
        ? {
            [NAV_ROW_ATTR]: "",
            tabIndex: -1,
            onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
              if (event.key === "Enter" && event.target === event.currentTarget)
                void handleClick(true);
            },
          }
        : {})}
      className={cn(
        // Synlig fokus (WCAG 2.4.7): ring i tillegg til bakgrunnen.
        clickable &&
          "cursor-pointer outline-none focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        isSelected && "bg-muted/70",
        className,
      )}
    >
      {hasBulkActions ? (
        <TableCell className="flex w-8" onClick={handleToggle}>
          <Checkbox
            checked={selectedIds?.includes(record.id)}
            onClick={handleToggle}
          />
        </TableCell>
      ) : null}
      {children}
    </TableRow>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPromise = (value: any): value is Promise<any> =>
  value && typeof value.then === "function";

const DataTableEmpty = () => {
  return (
    <Alert>
      <AlertDescription>No results found.</AlertDescription>
    </Alert>
  );
};

export interface DataTableProps<RecordType extends RaRecord = RaRecord>
  extends Partial<DataTableBaseProps<RecordType>> {
  children: ReactNode;
  className?: string;
  rowClassName?: (record: RecordType) => string | undefined;
  bulkActionButtons?: ReactNode;
  bulkActionsToolbar?: ReactNode;
  /** Første kolonne står fast ved vannrett rulling (smale skjermer). */
  stickyFirstColumn?: boolean;
}

export function DataTableColumn<
  RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
>(props: DataTableColumnProps<RecordType>) {
  const renderContext = useDataTableRenderContext();
  switch (renderContext) {
    case "columnsSelector":
      return <ColumnsSelectorItem<RecordType> {...props} />;
    case "header":
      return <DataTableHeadCell {...props} />;
    case "data":
      return <DataTableCell {...props} />;
  }
}

/**
 * Reorder children based on columnRanks
 *
 * Note that columnRanks may be shorter than the number of children
 */
const reorderChildren = (children: ReactNode, columnRanks: number[]) =>
  Children.toArray(children).reduce((acc: ReactNode[], child, index) => {
    const rank = columnRanks.indexOf(index);
    if (rank === -1) {
      // if the column is not in columnRanks, keep it at the same index
      acc[index] = child;
    } else {
      // if the column is in columnRanks, move it to the rank index
      acc[rank] = child;
    }
    return acc;
  }, []);

function DataTableHeadCell<
  RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
>(props: DataTableColumnProps<RecordType>) {
  const {
    disableSort,
    source,
    label,
    sortByOrder,
    className,
    headerClassName,
    resizable = true,
  } = props;

  const columnWidths = useContext(ColumnWidthContext);
  const sort = useDataTableSortContext();
  const { handleSort } = useDataTableCallbacksContext();
  const resource = useResourceContext();
  const translate = useTranslate();
  const translateLabel = useTranslateLabel();
  const { storeKey, defaultHiddenColumns } = useDataTableStoreContext();
  const [hiddenColumns] = useStore<string[]>(storeKey, defaultHiddenColumns);
  const isColumnHidden = hiddenColumns.includes(source!);
  if (isColumnHidden) return null;

  const nextSortOrder =
    sort && sort.field === source
      ? oppositeOrder[sort.order]
      : (sortByOrder ?? "ASC");
  const fieldLabel = translateLabel({
    label: typeof label === "string" ? label : undefined,
    resource,
    source,
  });
  const sortLabel = translate("ra.sort.sort_by", {
    field: fieldLabel,
    field_lower_first:
      typeof fieldLabel === "string"
        ? fieldLabel.charAt(0).toLowerCase() + fieldLabel.slice(1)
        : undefined,
    order: translate(`ra.sort.${nextSortOrder}`),
    _: translate("ra.action.sort"),
  });

  return (
    <TableHead
      className={cn("relative overflow-hidden", className, headerClassName)}
    >
      {handleSort && sort && !disableSort && source ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="-ml-3 -mr-3 h-8 data-[state=open]:bg-accent cursor-pointer"
                data-field={source}
                onClick={handleSort}
              >
                {headerClassName?.includes("text-right") ? null : (
                  <FieldTitle
                    label={label}
                    source={source}
                    resource={resource}
                  />
                )}
                {sort.field === source ? (
                  sort.order === "ASC" ? (
                    <ArrowDownAZ className="ml-2 h-6 w-6" />
                  ) : (
                    <ArrowUpZA className="ml-2 h-6 w-6" />
                  )
                ) : null}
                {headerClassName?.includes("text-right") ? (
                  <FieldTitle
                    label={label}
                    source={source}
                    resource={resource}
                  />
                ) : null}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{sortLabel}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <FieldTitle label={label} source={source} resource={resource} />
      )}
      {resizable && source && columnWidths ? (
        <ColumnResizeHandle onPointerDown={columnWidths.startResize(source)} />
      ) : null}
    </TableHead>
  );
}

const oppositeOrder: Record<SortPayload["order"], SortPayload["order"]> = {
  ASC: "DESC",
  DESC: "ASC",
};

function DataTableCell<
  RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
>(props: DataTableColumnProps<RecordType>) {
  const {
    children,
    render,
    field,
    source,
    className,
    cellClassName,
    conditionalClassName,
  } = props;

  const { storeKey, defaultHiddenColumns } = useDataTableStoreContext();
  const [hiddenColumns] = useStore<string[]>(storeKey, defaultHiddenColumns);
  const record = useRecordContext<RecordType>();
  const isColumnHidden = hiddenColumns.includes(source!);
  if (isColumnHidden) return null;
  if (!render && !field && !children && !source) {
    throw new Error(
      "DataTableColumn: Missing at least one of the following props: render, field, children, or source",
    );
  }

  const content =
    children ??
    (render
      ? record && render(record)
      : field
        ? createElement(field, { source })
        : get(record, source!));

  return (
    <TableCell
      className={cn(
        // overflow-hidden + ellipsis: med table-fixed kan innholdet være
        // bredere enn kolonnen, og skal da klippes, ikke sprenge cellen.
        "overflow-hidden py-1 text-ellipsis",
        className,
        cellClassName,
        record && conditionalClassName?.(record),
      )}
      // Ren tekst som klippes, vises i sin helhet når musa holdes over.
      title={
        typeof content === "string" || typeof content === "number"
          ? String(content)
          : undefined
      }
    >
      {content}
    </TableCell>
  );
}

export interface DataTableColumnProps<
  RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
> {
  className?: string;
  cellClassName?: string;
  headerClassName?: string;
  conditionalClassName?: (record: RecordType) => string | false | undefined;
  children?: ReactNode;
  render?: (record: RecordType) => React.ReactNode;
  field?: React.ElementType;
  source?: NoInfer<HintedString<ExtractRecordPaths<RecordType>>>;
  label?: React.ReactNode;
  disableSort?: boolean;
  sortByOrder?: SortPayload["order"];
  /** Standardbredde i piksler. Brukerens dratte bredde går foran denne. */
  width?: number;
  /** Slå av draghåndtaket. Kolonner uten `source` er aldri resizable. */
  resizable?: boolean;
  /** Innhold i sumraden under kolonnen, f.eks. en total. */
  footer?: ReactNode;
  footerClassName?: string;
}

export function DataTableNumberColumn<
  RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
>(props: DataTableNumberColumnProps<RecordType>) {
  const {
    source,
    options,
    locales,
    className,
    headerClassName,
    cellClassName,
    ...rest
  } = props;
  return (
    <DataTableColumn
      source={source}
      {...rest}
      className={className}
      headerClassName={cn("text-right", headerClassName)}
      cellClassName={cn("text-right", cellClassName)}
    >
      <NumberField source={source} options={options} locales={locales} />
    </DataTableColumn>
  );
}

export interface DataTableNumberColumnProps<
  RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
> extends DataTableColumnProps<RecordType> {
  source: NoInfer<HintedString<ExtractRecordPaths<RecordType>>>;
  locales?: string | string[];
  options?: Intl.NumberFormatOptions;
}
